import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Chart } from '../../components/Chart';
import { ChartTopBar } from '../../components/ChartTopBar';
import { ChartEditorSidebar } from '../../components/ChartEditorSidebar';
import { useAnalysis } from '../../hooks/useAnalysis';
import { EditChartModal } from '../../components/EditChartModal';
import { analysisService } from '../../services/analysis.service';
import type { Analysis } from '../../services/analysis.service';
import { toastService } from '../../services/toast.service';
import { Loader2 } from 'lucide-react';
import { useDataExplorer } from '../../contexts/DataExplorerContext';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';

import { mapDateRangeFromBackend, mapDateRangeToBackend } from '../../components/ui/date-range-picker';

export default function ChartDetail() {
  const { chartId } = useParams<{ chartId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isInDraftMode = useIsInDraftMode();
  const { data: analysis } = useAnalysis(chartId || null);
  const [workingAnalysis, setWorkingAnalysis] = useState<Analysis | null>(null);
  const [dateRange, setDateRange] = useState<string>('all_time');
  const [customDateStart, setCustomDateStart] = useState<string>('');
  const [customDateEnd, setCustomDateEnd] = useState<string>('');
  const [chartData, setChartData] = useState<Record<string, any>[]>([]);
  const [chartConfig, setChartConfig] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalDefaultTab, setEditModalDefaultTab] = useState<'basic' | 'metrics' | 'filters'>('basic');
  const refreshHandlerRef = useRef<((isApplyOperation?: boolean) => Promise<void>) | null>(null);
  const shouldTriggerRefreshRef = useRef<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(true);
  const [currentSortOrder, setCurrentSortOrder] = useState<any[] | undefined>(undefined);
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);

  // Use DataExplorer context for drilldowns
  const { openDataExplorer } = useDataExplorer();

  // Initialize working analysis from saved analysis
  useEffect(() => {
    if (analysis && !workingAnalysis) {
      setWorkingAnalysis(analysis);
    }
  }, [analysis, workingAnalysis]);

  // Update date range when analysis loads
  useEffect(() => {
    if (analysis?.date_range) {
      // If it's a custom date range and we have the dates, use 'custom'
      // Otherwise use the mapped date range
      if (analysis.date_range === 'CUSTOM' && analysis.custom_date_range_start && analysis.custom_date_range_end) {
        setDateRange('custom');
        setCustomDateStart(analysis.custom_date_range_start);
        setCustomDateEnd(analysis.custom_date_range_end);
      } else {
        setDateRange(mapDateRangeFromBackend(analysis.date_range) || 'all_time');
        setCustomDateStart('');
        setCustomDateEnd('');
      }
      isInitialLoadRef.current = false;
    }
  }, [analysis?.date_range, analysis?.custom_date_range_start, analysis?.custom_date_range_end]);

  // Trigger refresh when date range changes (but not on initial load)
  useEffect(() => {
    if (shouldTriggerRefreshRef.current && !isInitialLoadRef.current && refreshHandlerRef.current) {
      shouldTriggerRefreshRef.current = false;
      // Use setTimeout to ensure Chart component has updated with new dateRange
      setTimeout(() => {
        if (refreshHandlerRef.current) {
          refreshHandlerRef.current();
        }
      }, 100);
    }
  }, [dateRange, customDateStart, customDateEnd]);

  const handleDataLoaded = useCallback((data: Record<string, any>[], config: any) => {
    setChartData(data);
    setChartConfig(config);
  }, []);

  const handleDateRangeChange = useCallback((newDateRange: string, customStart?: string, customEnd?: string) => {
    setDateRange(newDateRange);
    if (newDateRange === 'custom' && customStart && customEnd) {
      setCustomDateStart(customStart);
      setCustomDateEnd(customEnd);
    } else {
      setCustomDateStart('');
      setCustomDateEnd('');
    }
    // Update workingAnalysis with the new date range so it gets saved
    setWorkingAnalysis(prev => {
      if (!prev) return prev;
      const backendRange = mapDateRangeToBackend(newDateRange);
      return {
        ...prev,
        date_range: backendRange,
        custom_date_range_start: newDateRange === 'custom' && customStart ? customStart : undefined,
        custom_date_range_end: newDateRange === 'custom' && customEnd ? customEnd : undefined,
      };
    });
    // Mark that we should trigger refresh after state updates
    shouldTriggerRefreshRef.current = true;
  }, []);

  const handleEdit = useCallback(() => {
    setEditModalDefaultTab('basic');
    setIsEditModalOpen(true);
  }, []);

  const handleFiltersClick = useCallback(() => {
    setEditModalDefaultTab('filters');
    setIsEditModalOpen(true);
  }, []);

  const activeFilterCount = useMemo(() => {
    return workingAnalysis?.dimension_filters?.filter((f: any) => f.dimension_id && f.comparator).length || 0;
  }, [workingAnalysis?.dimension_filters]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!analysis || !workingAnalysis) return false;
    
    // Check sort order changes
    const savedSortOrder = analysis.default_sort_order || [];
    const currentSortOrderStr = JSON.stringify(currentSortOrder || []);
    const savedSortOrderStr = JSON.stringify(savedSortOrder);
    const sortOrderChanged = currentSortOrderStr !== savedSortOrderStr;
    
    return (
      analysis.name !== workingAnalysis.name ||
      analysis.visualization_type !== workingAnalysis.visualization_type ||
      JSON.stringify(analysis.measure_ids) !== JSON.stringify(workingAnalysis.measure_ids) ||
      JSON.stringify(analysis.dimension_ids) !== JSON.stringify(workingAnalysis.dimension_ids) ||
      JSON.stringify(analysis.dimension_filters || []) !== JSON.stringify(workingAnalysis.dimension_filters || []) ||
      sortOrderChanged ||
      (analysis.limit || undefined) !== (workingAnalysis.limit || undefined) ||
      JSON.stringify(analysis.viz_options || {}) !== JSON.stringify(workingAnalysis.viz_options || {}) ||
      JSON.stringify(analysis.metric_filters || []) !== JSON.stringify(workingAnalysis.metric_filters || []) ||
      (analysis.date_range || undefined) !== (workingAnalysis.date_range || undefined) ||
      (analysis.custom_date_range_start || undefined) !== (workingAnalysis.custom_date_range_start || undefined) ||
      (analysis.custom_date_range_end || undefined) !== (workingAnalysis.custom_date_range_end || undefined)
    );
  }, [analysis, workingAnalysis, currentSortOrder]);

  const handleApplyChanges = useCallback((updatedAnalysis: Partial<Analysis>) => {
    if (!analysis) return;

    // Clear sort order if visualization type changed
    if (updatedAnalysis.visualization_type && updatedAnalysis.visualization_type !== analysis.visualization_type) {
      setCurrentSortOrder(undefined);
    }

    const merged = { ...analysis, ...updatedAnalysis } as Analysis;

    console.log('[ChartDetail] handleApplyChanges - server analysis:', { id: analysis.id, viz_options: analysis.viz_options, measure_ids: analysis.measure_ids });
    console.log('[ChartDetail] handleApplyChanges - updatedAnalysis:', { viz_options: (updatedAnalysis as any).viz_options, measure_ids: updatedAnalysis.measure_ids });
    console.log('[ChartDetail] handleApplyChanges - merged:', { id: merged.id, viz_options: merged.viz_options, measure_ids: merged.measure_ids });

    // Check if only viz_options changed (no data-affecting fields changed)
    const currentWorking = workingAnalysis || analysis;
    const onlyVizOptionsChanged =
      merged.visualization_type === currentWorking.visualization_type &&
      JSON.stringify(merged.measure_ids) === JSON.stringify(currentWorking.measure_ids) &&
      JSON.stringify(merged.dimension_ids) === JSON.stringify(currentWorking.dimension_ids) &&
      JSON.stringify(merged.dimension_filters || []) === JSON.stringify(currentWorking.dimension_filters || []) &&
      JSON.stringify(merged.metric_filters || []) === JSON.stringify(currentWorking.metric_filters || []);

    console.log('[ChartDetail] handleApplyChanges - currentWorking:', { viz_options: currentWorking.viz_options, measure_ids: currentWorking.measure_ids });
    console.log('[ChartDetail] handleApplyChanges - onlyVizOptionsChanged:', onlyVizOptionsChanged);

    setWorkingAnalysis(merged);

    if (onlyVizOptionsChanged) {
      // No data refetch needed — just re-render with the new viz_options
      console.log('[ChartDetail] handleApplyChanges - skipping refetch, only viz_options changed');
      setIsApplyingChanges(false);
      return;
    }

    setIsApplyingChanges(true);
    // Trigger refresh of chart data with isApplyOperation=true to use staleness threshold
    if (refreshHandlerRef.current) {
      setTimeout(() => {
        refreshHandlerRef.current?.(true); // Pass true to indicate this is an apply operation
      }, 100);
    }
  }, [analysis, workingAnalysis]);

  // Handle apply from sidebar
  const handleSidebarApply = useCallback((updatedAnalysis: Analysis, isApplying?: boolean) => {
    setIsApplyingChanges(isApplying || false);
    handleApplyChanges(updatedAnalysis);
  }, [handleApplyChanges]);

  // Create a default analysis object to ensure sidebar always has valid props
  const defaultAnalysis = useMemo<Analysis>(() => ({
    id: chartId || '',
    name: 'Untitled Chart',
    visualization_type: 'TABLE',
    dimension_ids: [],
    measure_ids: [],
    dimension_filters: [],
  }), [chartId]);

  // Use workingAnalysis, then analysis, then default
  const sidebarAnalysis = useMemo(() => {
    return workingAnalysis || analysis || defaultAnalysis;
  }, [workingAnalysis, analysis, defaultAnalysis]);

  const handleSave = useCallback(async () => {
    if (!chartId || !workingAnalysis) return;

    setIsSaving(true);
    try {
      // Destructure out dependency_graph as it shouldn't be sent back to the server
      const { dependency_graph, ...analysisData } = workingAnalysis as Analysis & { dependency_graph?: any };

      // Include sort order in the update
      const updates = {
        ...analysisData,
        default_sort_order: currentSortOrder || [],
      };
      
      // Just patch the analysis - don't trigger prep
      await analysisService.update(chartId, updates);
      // Invalidate and refetch the analysis data to get the updated saved state
      await queryClient.invalidateQueries({ queryKey: ['analysis', chartId] });
      await queryClient.invalidateQueries({ queryKey: ['analyses'] });

      // Update saved analysis state - this will trigger re-initialization from saved analysis
      setWorkingAnalysis(null);
      // Don't invalidate analysisData or call refreshHandler - prep happens when applying changes, not on save
      
      // Show success toast
      toastService.success('Chart saved successfully');
    } catch (error) {
      console.error('Failed to save chart:', error);
      toastService.error('Failed to save chart', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    } finally {
      setIsSaving(false);
    }
  }, [chartId, workingAnalysis, currentSortOrder, queryClient]);

  useUnsavedChangesGuard(hasUnsavedChanges, handleSave);

  const handleDelete = useCallback(async () => {
    if (!chartId) return;
    await analysisService.delete(chartId);
    await queryClient.invalidateQueries({ queryKey: ['analyses'] });
    toastService.success('Chart deleted successfully');
    navigate('/analytics');
  }, [chartId, queryClient, navigate]);

  const handleAddToDashboard = useCallback(() => {
    // Open add to dashboard dialog
    console.log('Add to dashboard:', chartId);
    // TODO: Implement add to dashboard functionality
  }, [chartId]);

  const handleShare = useCallback(() => {
    // Copy shareable link to clipboard
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      // TODO: Show toast notification
      console.log('Link copied to clipboard');
    });
  }, []);


  const handleRefreshReady = useCallback((handler: (isApplyOperation?: boolean) => Promise<void>) => {
    refreshHandlerRef.current = handler;
  }, []);

  const handleRename = useCallback((newName: string) => {
    if (!workingAnalysis) return;
    // Update workingAnalysis instead of saving immediately
    // This will trigger hasUnsavedChanges and activate the save button
    setWorkingAnalysis({ ...workingAnalysis, name: newName });
  }, [workingAnalysis]);

  const handleSortChange = useCallback((sortOrder: any[] | undefined) => {
    setCurrentSortOrder(sortOrder);
  }, []);

  // Handle drilldown from chart - use context
  const handleDrillDown = useCallback((updatedAnalysis: Analysis) => {
    openDataExplorer({
      analysis: updatedAnalysis,
      originalAnalysisId: chartId || '',
      dateRange,
      customDateStart,
      customDateEnd,
      onDateRangeChange: handleDateRangeChange,
    });
  }, [openDataExplorer, chartId, dateRange, customDateStart, customDateEnd, handleDateRangeChange]);

  if (!chartId) {
    return (
      <div className="p-8">
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">No chart ID provided</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full min-w-0 flex flex-col bg-background" style={{ height: 'calc(100vh)' }}>
      <ChartTopBar
        chartName={workingAnalysis?.name || analysis?.name || 'Untitled Chart'}
        chartType={workingAnalysis?.visualization_type || analysis?.visualization_type}
        chartData={chartData}
        chartConfig={chartConfig}
        dateRange={dateRange}
        customDateStart={customDateStart}
        customDateEnd={customDateEnd}
        onDateRangeChange={handleDateRangeChange}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSave}
        isSaving={isSaving}
        onRename={handleRename}
        onDelete={isInDraftMode ? handleDelete : undefined}
      />
      <div className="flex-1 flex overflow-hidden min-w-0 w-full max-w-full">
        {/* Sidebar - Always render when chartId exists to maintain hook order */}
        {chartId && (
          <div className="w-[20%] min-w-[250px] flex-shrink-0">
            <ChartEditorSidebar
              analysis={sidebarAnalysis}
              originalAnalysisId={chartId}
              dateRange={dateRange}
              customDateStart={customDateStart}
              customDateEnd={customDateEnd}
              onDateRangeChange={handleDateRangeChange}
              onApply={handleSidebarApply}
            />
          </div>
        )}

        {/* Chart Content */}
        <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto p-8">
          {isSaving ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Applying changes...</p>
              </div>
            </div>
          ) : (
            <Chart
              analysisId={chartId}
              workingAnalysis={workingAnalysis}
              dateRange={dateRange}
              customDateStart={customDateStart}
              customDateEnd={customDateEnd}
              onDataLoaded={handleDataLoaded}
              onRefreshReady={handleRefreshReady}
              onSortChange={handleSortChange}
              initialSortOrder={analysis?.default_sort_order}
              isApplyingChanges={isApplyingChanges}
              onApplyChangesComplete={() => setIsApplyingChanges(false)}
              onDrillDown={handleDrillDown}
              disableSave={true}
            />
          )}
        </div>
      </div>
      {chartId && (
        <EditChartModal
          chartId={chartId}
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          onApplyChanges={handleApplyChanges}
          defaultTab={editModalDefaultTab}
        />
      )}
    </div>
  );
}

